import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {MutationObserver | undefined} */
  #accountTextObserver;

  /** @type {MutationObserver | undefined} */
  #accountFocusOutlineObserver;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    window.addEventListener('resize', this.#syncAccountDialogLayout);
    window.addEventListener('resize', this.#syncCartTriggerAccessibleName);
    this.#syncCartTriggerAriaExpanded();
    this.#syncCartTriggerAccessibleName();
    this.#normalizeAccountHeading();
    this.#setupAccountFocusRestore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    window.removeEventListener('resize', this.#syncAccountDialogLayout);
    window.removeEventListener('resize', this.#syncCartTriggerAccessibleName);
    this.#accountTextObserver?.disconnect();
    this.#accountFocusOutlineObserver?.disconnect();
    if (this.#refreshAccountFocusOutlines) {
      document.removeEventListener('shopify-render', this.#refreshAccountFocusOutlines);
    }
  }

  #syncAccountDialogLayout = () => {
    const shadowRoot = this.querySelector('shopify-account')?.shadowRoot;
    const dialog = shadowRoot?.querySelector('[part~="dialog"], dialog, [popover]');
    const cartButton = this.querySelector('[data-testid="cart-drawer-trigger"], .action__cart');

    if (!(dialog instanceof HTMLElement) || !(cartButton instanceof HTMLElement)) return;

    const rightOffset = Math.max(0, window.innerWidth - cartButton.getBoundingClientRect().right);
    dialog.style.insetInlineStart = 'auto';
    dialog.style.insetInlineEnd = `${rightOffset}px`;
    dialog.style.boxShadow = 'none';
  };

  #normalizeAccountHeading = async () => {
    await customElements.whenDefined('shopify-account');
    if (!this.isConnected) return;

    const shadowRoot = this.querySelector('shopify-account')?.shadowRoot;
    if (!shadowRoot) return;

    const updateHeading = () => {
      const walker = document.createTreeWalker(shadowRoot, NodeFilter.SHOW_TEXT);
      let textNode;

      while ((textNode = walker.nextNode())) {
        if (textNode.nodeValue?.trim().toLowerCase() !== 'sign in or create account') continue;
        textNode.nodeValue = textNode.nodeValue.replace(
          /sign in or create account/i,
          'Sign in or Create an Account'
        );
        if (textNode.parentElement) textNode.parentElement.style.fontWeight = '700';
      }

      this.#syncAccountDialogLayout();
    };

    updateHeading();
    this.#applyAccountInputBorderContrast(shadowRoot);
    this.#setupAccountFocusOutline(shadowRoot);
    this.#accountTextObserver = new MutationObserver(updateHeading);
    this.#accountTextObserver.observe(shadowRoot, { childList: true, subtree: true, characterData: true });
  };

  /**
   * @returns {{ inner: string, outer: string, width: string, offset: string, outerWidth: string }}
   */
  #getKilwinsFocusRingTokens() {
    const root = getComputedStyle(document.documentElement);

    return {
      inner: root.getPropertyValue('--kilwins-focus-ring-inner').trim() || '#fff',
      outer:
        root.getPropertyValue('--kilwins-focus-ring-outer').trim() ||
        root.getPropertyValue('--color-primary-button-background').trim() ||
        '#7A1622',
      width: root.getPropertyValue('--kilwins-focus-ring-width').trim() || '2px',
      offset: root.getPropertyValue('--kilwins-focus-ring-offset').trim() || '4px',
      outerWidth: root.getPropertyValue('--kilwins-focus-ring-outer-width').trim() || '2px',
    };
  }

  #buildAccountFocusOutlineCSS() {
    const { inner, outer, width, offset, outerWidth } = this.#getKilwinsFocusRingTokens();
    const ringInset = `calc(-1 * (${offset} + ${width} + ${outerWidth}))`;
    const focusSelectors = [
      '.account__item',
      '.stack-inline .button.outline',
      '.account__shortcuts .button.outline',
      '.button.outline',
      'a.button',
      'button.button',
    ]
      .flatMap((selector) => [`${selector}:focus-visible`, `${selector}:focus`])
      .join(',\n      ');
    const afterSelectors = focusSelectors
      .replace(/:focus-visible/g, ':focus-visible::after')
      .replace(/:focus(?!-)/g, ':focus::after');

    return `
      ${focusSelectors} {
        --indent: 0 !important;
        position: relative !important;
        overflow: visible !important;
        outline: ${width} solid ${inner} !important;
        outline-offset: ${offset} !important;
        background: none !important;
        background-color: transparent !important;
        box-shadow: none !important;
      }

      ${afterSelectors} {
        content: '' !important;
        position: absolute !important;
        pointer-events: none !important;
        border-radius: inherit !important;
        inset: ${ringInset} !important;
        border: ${outerWidth} solid ${outer} !important;
      }
    `;
  }

  /**
   * Append Kilwins focus styles last in adoptedStyleSheets so they override
   * Shopify's account sheet CSS (also injected via adoptedStyleSheets).
   * @param {ShadowRoot} shadowRoot
   */
  #attachAccountFocusOutlineSheet(shadowRoot) {
    const css = this.#buildAccountFocusOutlineCSS();
    let sheet = shadowRoot.kilwinsAccountMenuFocusSheet;

    if (!sheet) {
      sheet = new CSSStyleSheet();
      shadowRoot.kilwinsAccountMenuFocusSheet = sheet;
    }

    sheet.replaceSync(css);
    shadowRoot.adoptedStyleSheets = [
      ...shadowRoot.adoptedStyleSheets.filter((existing) => existing !== sheet),
      sheet,
    ];
  }

  /**
   * Shopify account + login-form-loader use nested shadow roots.
   * @param {ShadowRoot} shadowRoot
   */
  #applyAccountFocusOutlineRecursive(shadowRoot) {
    this.#attachAccountFocusOutlineSheet(shadowRoot);

    shadowRoot.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) {
        this.#applyAccountFocusOutlineRecursive(element.shadowRoot);
      }
    });
  }

  #refreshAccountFocusOutlines = () => {
    if (!this.isConnected) return;

    const shadowRoot = this.querySelector('shopify-account')?.shadowRoot;
    if (!shadowRoot) return;

    this.#applyAccountFocusOutlineRecursive(shadowRoot);
  };

  /**
   * Account menu links use a subdued grey focus fill (~1.12:1 on white). Shopify
   * loads dialog CSS after theme init — re-apply on open and menu render.
   * @param {ShadowRoot} shadowRoot
   */
  #setupAccountFocusOutline(shadowRoot) {
    if (shadowRoot.kilwinsAccountFocusOutlineSetup) return;
    shadowRoot.kilwinsAccountFocusOutlineSetup = true;

    const account = this.querySelector('shopify-account');
    const scheduleRefresh = () => {
      requestAnimationFrame(() => this.#refreshAccountFocusOutlines());
    };

    scheduleRefresh();
    account?.addEventListener('open', scheduleRefresh);
    document.addEventListener('shopify-render', scheduleRefresh);

    this.#accountFocusOutlineObserver = new MutationObserver(scheduleRefresh);
    this.#accountFocusOutlineObserver.observe(shadowRoot, { childList: true, subtree: true });
  }

  /**
   * Shopify's account sheet default input border (#dedbda) fails 3:1 on #fdfdfd.
   * Host tokens may not reach unfocused inputs — reinforce inside the shadow root.
   * @param {ShadowRoot} shadowRoot
   */
  #applyAccountInputBorderContrast(shadowRoot) {
    if (shadowRoot.kilwinsAccountInputBorder) return;
    shadowRoot.kilwinsAccountInputBorder = true;

    const account = this.querySelector('shopify-account');
    const borderColor =
      (account instanceof HTMLElement &&
        getComputedStyle(account).getPropertyValue('--shopify-account-color-border').trim()) ||
      getComputedStyle(document.documentElement).getPropertyValue('--kilwins-muted').trim() ||
      '#767676';

    const style = document.createElement('style');
    style.textContent = `
      input:not([type='checkbox']):not([type='radio']),
      textarea,
      select {
        border-color: ${borderColor} !important;
      }
    `;
    shadowRoot.appendChild(style);
  }

  #setupAccountFocusRestore = async () => {
    await customElements.whenDefined('shopify-account');
    if (!this.isConnected) return;

    const account = this.querySelector('shopify-account');
    const shadowRoot = account?.shadowRoot;
    if (!shadowRoot || shadowRoot.kilwinsAccountFocusRestore) return;
    shadowRoot.kilwinsAccountFocusRestore = true;

    /** @type {HTMLElement | null} */
    let accountTrigger = null;

    const getAccountTrigger = () => {
      const button =
        shadowRoot.querySelector('button[popovertarget], button[aria-haspopup]') ?? shadowRoot.querySelector('button');
      return button instanceof HTMLElement ? button : null;
    };

    const restoreAccountFocus = () => {
      const focusTarget = accountTrigger?.isConnected ? accountTrigger : getAccountTrigger();
      if (!(focusTarget instanceof HTMLElement)) return;

      requestAnimationFrame(() => {
        focusTarget.focus({ preventScroll: true });
      });
      accountTrigger = null;
    };

    const popover = shadowRoot.querySelector('[popover]');
    popover?.addEventListener('toggle', (event) => {
      const toggleEvent = /** @type {ToggleEvent} */ (event);
      if (toggleEvent.newState === 'open') {
        accountTrigger = getAccountTrigger();
      } else if (toggleEvent.newState === 'closed') {
        restoreAccountFocus();
      }
    });

    const dialog = shadowRoot.querySelector('dialog');
    if (dialog) {
      const observer = new MutationObserver(() => {
        if (dialog.open) {
          accountTrigger = getAccountTrigger();
        } else if (accountTrigger) {
          restoreAccountFocus();
        }
      });
      observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
  };

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Desktop text cart CTA exposes count in visible label text; icon/mobile uses
   * aria-label + aria-describedby so the count is not overridden or lost.
   */
  #syncCartTriggerAccessibleName = () => {
    const trigger = this.querySelector('[data-testid="cart-drawer-trigger"], .action__cart');
    if (!trigger) return;

    const isTextDesktop =
      this.classList.contains('header-actions--text') && window.matchMedia('(min-width: 750px)').matches;

    if (isTextDesktop) {
      trigger.removeAttribute('aria-label');
      trigger.removeAttribute('aria-describedby');
      return;
    }

    trigger.setAttribute('aria-label', Theme.translations?.cart ?? 'Cart');
    trigger.setAttribute('aria-describedby', 'cart-bubble-text');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const cartCount = cart?.totalQuantity ?? detail?.itemCount;
        if (cartCount === undefined) return;

        const cartDrawer = document.getElementById('cart-drawer');
        if (!cartDrawer?.hasAttribute('open')) {
          this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
        }

        this.#syncCartTriggerAccessibleName();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
